import { DRAW_POSITIONS, THEME_DEFINITIONS } from './tarot-deck.js';

export const SESSION_VERSION = 2;
export const SESSION_TTL_MS = 2 * 60 * 60 * 1000;

const VALID_THEME_IDS = new Set(['', ...THEME_DEFINITIONS.map(({ id }) => id)]);
const VALID_DEPTHS = new Set(['', 'Basic', 'Focused', 'In-Depth']);
const VALID_DELIVERIES = new Set(['', '24 hours', '12 hours', '6 hours']);

export const READING_OPTION_NAMES = Object.freeze(['Reading depth', 'Delivery', 'Theme']);
export const READING_VARIANT_MATRIX = Object.freeze([
  Object.freeze({ depth: 'Basic', delivery: '24 hours', price: 599 }),
  Object.freeze({ depth: 'Basic', delivery: '12 hours', price: 699 }),
  Object.freeze({ depth: 'Basic', delivery: '6 hours', price: 799 }),
  Object.freeze({ depth: 'Focused', delivery: '24 hours', price: 999 }),
  Object.freeze({ depth: 'Focused', delivery: '12 hours', price: 1099 }),
  Object.freeze({ depth: 'Focused', delivery: '6 hours', price: 1199 }),
  Object.freeze({ depth: 'In-Depth', delivery: '24 hours', price: 1699 }),
  Object.freeze({ depth: 'In-Depth', delivery: '12 hours', price: 1799 }),
  Object.freeze({ depth: 'In-Depth', delivery: '6 hours', price: 1899 })
]);

const EXPECTED_VARIANT_PRICES = new Map(
  READING_VARIANT_MATRIX.map(({ depth, delivery, price }) => [`${depth}\u0000${delivery}`, price])
);
const READING_THEME_LABELS = Object.freeze(THEME_DEFINITIONS.map(({ label }) => label));
const EXPECTED_VARIANT_COMBINATIONS = new Set(
  READING_THEME_LABELS.flatMap((theme) => (
    READING_VARIANT_MATRIX.map(({ depth, delivery }) => `${depth}\u0000${delivery}\u0000${theme}`)
  ))
);
const VARIANT_CONFIGURATION_MESSAGE = 'Configure exactly 63 available variants with Shopify options Reading depth, Delivery, and Theme, using the approved values and prices.';

export function sessionHasExpired(state, now = Date.now()) {
  return !Number.isFinite(state?.expiresAt) || state.expiresAt <= now;
}

function unitInterval(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 0.9999999999999999);
}

export function secureRandomUnit() {
  if (globalThis.crypto?.getRandomValues) {
    const value = new Uint32Array(1);
    globalThis.crypto.getRandomValues(value);
    return value[0] / 2 ** 32;
  }
  return Math.random();
}

export function orientationFromUnit(value) {
  return unitInterval(value) < 0.15 ? 'reversed' : 'upright';
}

export function drawSpread(deck, randomUnit = secureRandomUnit, excludedCardIds = []) {
  if (!Array.isArray(deck)) {
    throw new TypeError('A tarot deck needs at least three cards.');
  }

  const excluded = new Set(excludedCardIds);
  const remaining = deck.filter((card) => !excluded.has(card.id));
  if (remaining.length < DRAW_POSITIONS.length) {
    throw new TypeError('At least three eligible tarot cards are required.');
  }
  return DRAW_POSITIONS.map((position) => {
    const index = Math.floor(unitInterval(randomUnit()) * remaining.length);
    const [card] = remaining.splice(index, 1);
    return {
      position,
      card,
      orientation: orientationFromUnit(randomUnit())
    };
  });
}

export function normalizeQuestion(value) {
  return String(value ?? '').trim().replace(/\s+/g, ' ');
}

export function validateQuestion(value) {
  const length = normalizeQuestion(value).length;
  return length >= 12 && length <= 600 ? '' : 'Use 12–600 characters.';
}

export function validateContext(value) {
  return String(value ?? '').length <= 1500 ? '' : 'Use no more than 1,500 characters.';
}

export function createBriefSignature({ theme = '', question = '', context = '' } = {}) {
  return JSON.stringify([
    String(theme).trim(),
    normalizeQuestion(question),
    String(context).trim()
  ]);
}

export function validateReadingVariantMatrix(variants, optionNames = READING_OPTION_NAMES) {
  if (
    !Array.isArray(optionNames)
    || optionNames.length !== READING_OPTION_NAMES.length
    || optionNames.some((name, index) => name !== READING_OPTION_NAMES[index])
    || !Array.isArray(variants)
    || variants.length !== EXPECTED_VARIANT_COMBINATIONS.size
  ) return VARIANT_CONFIGURATION_MESSAGE;

  const combinations = new Set();
  const variantIds = new Set();
  for (const variant of variants) {
    if (
      !variant
      || variant.id === undefined
      || variantIds.has(String(variant.id))
      || variant.available !== true
      || !Array.isArray(variant.options)
      || variant.options.length !== READING_OPTION_NAMES.length
      || typeof variant.priceLabel !== 'string'
      || !variant.priceLabel.trim()
    ) return VARIANT_CONFIGURATION_MESSAGE;

    const priceKey = `${variant.options[0]}\u0000${variant.options[1]}`;
    const combinationKey = `${priceKey}\u0000${variant.options[2]}`;
    if (
      combinations.has(combinationKey)
      || !EXPECTED_VARIANT_COMBINATIONS.has(combinationKey)
      || variant.price !== EXPECTED_VARIANT_PRICES.get(priceKey)
    ) return VARIANT_CONFIGURATION_MESSAGE;
    combinations.add(combinationKey);
    variantIds.add(String(variant.id));
  }

  return combinations.size === EXPECTED_VARIANT_COMBINATIONS.size ? '' : VARIANT_CONFIGURATION_MESSAGE;
}

export function findReadingVariant(variants, depth, delivery, theme, optionNames = READING_OPTION_NAMES) {
  if (validateReadingVariantMatrix(variants, optionNames)) return null;
  const expected = [depth, delivery, theme].map((value) => String(value ?? '').trim().toLowerCase());
  if (expected.some((value) => !value)) return null;
  return variants.find((variant) => {
    return variant.options.every((value, index) => (
      String(value).trim().toLowerCase() === expected[index]
    ));
  }) ?? null;
}

function cardSentence(entry) {
  const keywords = entry.orientation === 'reversed' ? entry.card.reversed : entry.card.upright;
  const orientation = entry.orientation === 'reversed' ? ' reversed' : '';
  return `${entry.position}: ${entry.card.name}${orientation} brings ${keywords[0]} and ${keywords[1]} into view`;
}

export function createPreview({ theme, question, spread }) {
  const normalizedQuestion = normalizeQuestion(question);
  if (!theme || validateQuestion(normalizedQuestion) || !Array.isArray(spread) || spread.length !== 3) return '';
  const movement = spread.map(cardSentence).join('; ');
  return `For your ${theme.label} question — “${normalizedQuestion}” — ${movement}. Read together, the spread points to a pattern worth observing and one next step worth testing against real evidence. This is a reflective first layer, not a fixed prediction.`;
}

export function serializeSession(state) {
  return JSON.stringify(state);
}

function validSpread(spread, deckIds) {
  if (!Array.isArray(spread) || ![0, 3].includes(spread.length)) return false;
  const ids = new Set();
  return spread.every((entry) => {
    if (!entry || !deckIds.has(entry.cardId) || !['upright', 'reversed'].includes(entry.orientation)) return false;
    ids.add(entry.cardId);
    return true;
  }) && ids.size === spread.length;
}

function validRevealedPositions(value, spread) {
  if (!Array.isArray(value)) return false;
  const spreadPositions = new Set(DRAW_POSITIONS.slice(0, spread.length));
  const positions = new Set(value);
  return positions.size === value.length
    && value.every((position) => spreadPositions.has(position));
}

function validPreviousCardIds(value, deckIds) {
  if (!Array.isArray(value)) return false;
  const ids = new Set(value);
  return ids.size === value.length && value.every((cardId) => deckIds.has(cardId));
}

function validDrawTimestamp(value, spread) {
  if (typeof value !== 'string') return false;
  if (spread.length === 0) return value === '';
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

function canonicalStoredBriefSignature(value) {
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed) || parsed.length !== 3) return '';
    const [theme, question, context] = parsed;
    if (typeof theme !== 'string' || typeof question !== 'string' || typeof context !== 'string') return '';

    const canonicalTheme = theme.trim();
    const canonicalQuestion = normalizeQuestion(question);
    const canonicalContext = context.trim();
    if (
      !canonicalTheme
      || !VALID_THEME_IDS.has(canonicalTheme)
      || validateQuestion(canonicalQuestion)
      || validateContext(canonicalContext)
    ) return '';

    const canonical = createBriefSignature({
      theme: canonicalTheme,
      question: canonicalQuestion,
      context: canonicalContext
    });
    const legacy = JSON.stringify([canonicalTheme, question.trim(), canonicalContext]);
    return value === canonical || value === legacy ? canonical : '';
  } catch {
    return '';
  }
}

export function restoreSession(raw, deck, now = Date.now()) {
  try {
    const state = JSON.parse(raw);
    const deckIds = new Set(deck.map((card) => card.id));
    const hasSpread = state?.spread?.length === DRAW_POSITIONS.length;
    const allRevealed = hasSpread && state.revealedPositions?.length === DRAW_POSITIONS.length;
    const hasPackage = Boolean(state?.depth || state?.delivery);
    const canonicalQuestion = normalizeQuestion(state?.question);
    const canonicalSpreadSignature = hasSpread
      ? canonicalStoredBriefSignature(state?.spreadBriefSignature)
      : '';
    const packageIsConsistent = allRevealed
      ? Boolean(state.depth && state.delivery)
      : !hasPackage;
    const spreadStateIsConsistent = hasSpread
      ? !validateQuestion(canonicalQuestion)
        && !validateContext(state.context)
        && Boolean(canonicalSpreadSignature)
        && packageIsConsistent
      : state.revealedPositions?.length === 0
        && state.previousCardIds?.length === 0
        && state.spreadBriefSignature === ''
        && !hasPackage
        && state.drawnAt === '';

    if (
      !state
      || state.version !== SESSION_VERSION
      || sessionHasExpired(state, now)
      || typeof state.sessionId !== 'string'
      || !state.sessionId.trim()
      || typeof state.theme !== 'string'
      || !VALID_THEME_IDS.has(state.theme)
      || typeof state.question !== 'string'
      || typeof state.context !== 'string'
      || typeof state.depth !== 'string'
      || !VALID_DEPTHS.has(state.depth)
      || typeof state.delivery !== 'string'
      || !VALID_DELIVERIES.has(state.delivery)
      || !validSpread(state.spread, deckIds)
      || (state.spread.length > 0 && !state.theme)
      || !validRevealedPositions(state.revealedPositions, state.spread)
      || !validPreviousCardIds(state.previousCardIds, deckIds)
      || typeof state.spreadBriefSignature !== 'string'
      || !validDrawTimestamp(state.drawnAt, state.spread)
      || !spreadStateIsConsistent
    ) return null;
    return {
      ...state,
      question: canonicalQuestion,
      spreadBriefSignature: canonicalSpreadSignature
    };
  } catch {
    return null;
  }
}

export function createSessionId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `reading-${Date.now()}-${Math.floor(secureRandomUnit() * 1_000_000)}`;
}
